import {
    ChangeDetectorRef,
    Component,
    ViewChild,
    ViewEncapsulation,
} from '@angular/core';
import { CdftStatus, CDFTTable } from 'app/modules/cdft/model';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { CDFTService } from 'app/modules/cdft/services/cdft.service';
import { Router } from '@angular/router';
import { handleFormatDate } from 'app/core/shared/helper/date.helper';
import module from 'app/modules/cdft/cdft.module';
import { EMPTY, map, merge, Subject, switchMap, takeUntil } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { DEFAULT_COLUMN_LABELS } from 'app/core/shared/constant/column-label';
import { UserService } from 'app/core/user/user.service';
import { CdftSearchStateService } from '../../state/search.state';
import { ApplySortPayload } from 'app/core/shared/component/sort-menu/sort-menu.component';
import { OfflineDetectionService } from 'app/core/services/offline-detection.service';
import { OfflineSyncModalComponent } from 'app/modules/cdft/components/offline-sync-modal/offline-sync-modal.component';

@Component({
    selector: 'app-cdft-index',
    templateUrl: './index.page.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: module,
    styleUrls: ['./index.page.scss'],
})
export class CDFTIndexPage {
    @ViewChild(MatPaginator) private _paginator: MatPaginator;
    @ViewChild(MatSort) private _sort: MatSort;
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    searchForm: UntypedFormGroup;
    dt: any;
    startingIndex: number = 0;
    isLoading: boolean = false;
    cdftStatus = CdftStatus;
    isOnline$ = this.offlineService.isOnline$;
    isOnline: boolean = true;

    sortInfo: string = 'Created Date';
    private columnLabels: { [key: string]: string } = {
        ...DEFAULT_COLUMN_LABELS,
        result: 'Inspection Result',
        wfamStatus: 'WFM Status',
        tester: 'Tester',
        date: 'Inspection Date',
        status: 'Status',
    };

    columnSort = Object.entries(this.columnLabels).map(([key, name]) => ({
        key,
        name,
        isChecked: false, // default unchecked
    }));

    constructor(
        private service: CDFTService,
        private _formBuilder: UntypedFormBuilder,
        private router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private userService: UserService,
        private readonly state: CdftSearchStateService,
        private readonly offlineService: OfflineDetectionService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnInit() {
        this.setSearch();
        this.setDataTable();

        // Subscribe to online status
        this.isOnline$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe((isOnline) => {
                this.isOnline = isOnline;
                if (isOnline) {
                    // Sync handled by CDFTService globally
                }
                // Always reload data when online/offline status changes
                this.setPage({ offset: 0 });
            });
    }

    ngAfterViewInit() {
        if (this._sort && this._paginator) {
            // Set the initial sort
            this._sort.sort({
                id: 'field',
                start: 'asc',
                disableClear: true,
            });

            // Mark for check
            this._changeDetectorRef.markForCheck();

            // If the user changes the sort order...
            this._sort.sortChange
                .pipe(takeUntil(this._unsubscribeAll))
                .subscribe(() => {
                    // Reset back to the first page
                    this._paginator.pageIndex = 0;

                    this.onSort(this._sort);
                });

            // Get platform types if sort or page changes
            merge(this._sort.sortChange, this._paginator.page)
                .pipe(
                    switchMap(() => {
                        // this.isLoading = true;

                        let pageInfo = {
                            count: this._paginator.length,
                            pageSize: this._paginator.pageSize,
                            limit: this._paginator.pageSize,
                            offset: this._paginator.pageIndex,
                        };
                        this.setPage(pageInfo); // Emit page number

                        return EMPTY;
                    }),
                    map(() => {
                        this.isLoading = false;
                    }),
                )
                .subscribe();
        }
    }

    setSearch() {
        this.sortInfo = 'CDFT Date';
        this.searchForm = this._formBuilder.group({
            Page: [0],
            PageSize: [20],
            OrderBy1: [null],
            OrderBy2: [null],
            OrderBy3: [null],
            OrderDir: 'asc',
            WellName: [null],
            PlatformName: [null],
            StringName: [null],
            FieldName: [null],
        });

        this.searchForm
            .get('PageSize')
            .valueChanges.subscribe((value) => this.setSize(value));
    }

    setSize(size: number) {
        this.dt.page.size = size;
        this.dt.page.offset = 0;
        this.setPage({ offset: this.dt.page.offset });
    }

    setPage(pageInfo) {
        const saved = this.state.get();
        let params: any = {};

        // if have saved search
        if (saved) {
            // is search for changes
            const useSaved = !this.searchForm.dirty;

            this.searchForm.patchValue(
                useSaved ? saved : { Page: pageInfo.offset + 1 },
                { emitEvent: !useSaved },
            );

            this.dt.page.offset = pageInfo.offset;
            this.startingIndex =
                this.dt.page.offset *
                (useSaved ? saved.PageSize : this.dt.page.size);
            params = useSaved ? saved : this.searchForm.value;
        } else {
            this.dt.page.offset = pageInfo.offset;
            this.searchForm.patchValue({ Page: this.dt.page.offset + 1 });
            this.startingIndex = this.dt.page.offset * this.dt.page.size;
            params = this.searchForm.value;
        }

        const search: any = {
            wellLongName: params.Well?.wellLongName,
            stringShortName: params.String?.stringShortName,
            platformLongName: params.Platform?.platformLongName,
            fieldLongName: params.Field?.fieldLongName,
        };

        this.service
            .getCdftTable({ ...params, search })
            .subscribe((pagedData) => {
                this.dt.page.totalPages = pagedData.data.totalPages;
                // Handle both online (totalRecords) and offline (count) response formats
                this.dt.page.totalRecords =
                    pagedData.data.totalRecords ||
                    (pagedData.data as any).count ||
                    0;
                this.dt.rows = pagedData.data.data.map((record) => {
                    record.date = handleFormatDate(record.date, false);
                    return record;
                });
            });
    }

    downloadExcel() {
        this.service.downloadExcel();
    }

    setDataTable() {
        this.dt = {
            page: {
                size: this.searchForm.get('PageSize')?.value,
                totalRecords: 0,
                totalPages: 0,
                offset: 0,
            },
            rows: [],
        };
    }

    onSort(sort: any) {
        if (!sort) return;
        this.searchForm.patchValue({
            OrderBy1: sort.active,
            OrderDir: sort.direction,
        });

        this.sortInfo = this.columnLabels[sort.active] || sort.active;
        this.setPage({ offset: 0 });
    }

    reset() {
        this.state.clear(); // clear session storage
        this.setSearch();
        this.setPage({ offset: 0 });
    }

    update(row: CDFTTable) {
        if (this.searchForm.dirty) this.state.set(this.searchForm.value);
        this.router.navigate([`/data-management/cdft/${row.id}/edit`]);
    }

    view(row: CDFTTable) {
        if (this.searchForm.dirty) this.state.set(this.searchForm.value);
        this.router.navigate([`/data-management/cdft/${row.id}`]);
    }

    hasCdftEditRoles(): boolean {
        // roles that can edit cdft
        let hasRole = false;

        const roles = new Set([
            'Well Integrity Engineer',
            'Well Integrity Desk Engineer',
            'Offshore Personnel',
        ]);

        this.userService.roles$
            .subscribe((userRoles) => {
                hasRole = userRoles.some((r) => roles.has(r));
            })
            .unsubscribe();
        return hasRole;
    }

    handleSearch(event$) {
        const field = event$?.Field;
        const platform = event$?.Platform;
        const well = event$?.Well;
        const string = event$?.String;

        if (
            [field, platform, well, string].some((v) => v != null && v !== '')
        ) {
            this.setPage({ offset: 0 });
        }
    }

    applySort(payload: ApplySortPayload) {
        // Patch form (fill missing with null)
        const [OrderBy1, OrderBy2, OrderBy3] = [
            payload.columns[0]?.key ?? null,
            payload.columns[1]?.key ?? null,
            payload.columns[2]?.key ?? null,
        ];

        this.searchForm.patchValue({
            OrderBy1,
            OrderBy2,
            OrderBy3,
            OrderDir: payload.orderDir ?? 'asc',
        });

        const labels = payload.columns.map((k) => k.name);
        this.sortInfo = labels.length ? labels.join(' → ') : 'No sorting';
        this.setPage({ offset: 0 });
    }

    openOfflineSyncModal(): void {
        // Fetch all records without pagination for offline sync
        const allRecordsParams = {
            ...this.searchForm.value,
            PageSize: 9999, // Get all records
            Page: 1,
        };

        const search: any = {
            wellLongName: allRecordsParams.Well?.wellLongName,
            stringShortName: allRecordsParams.String?.stringShortName,
            platformLongName: allRecordsParams.Platform?.platformLongName,
            fieldLongName: allRecordsParams.Field?.fieldLongName,
        };

        this.service
            .getCdftTable({ ...allRecordsParams, search })
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe({
                next: (pagedData) => {
                    const allRecords = pagedData.data.data;
                    if (!allRecords || allRecords.length === 0) {
                        alert('No records available to sync');
                        return;
                    }

                    this.dialog.open(OfflineSyncModalComponent, {
                        width: '600px',
                        disableClose: false,
                        data: {
                            cdftRecords: allRecords,
                        },
                    });
                },
                error: (err) => {
                    console.error('Error fetching records for sync:', err);
                    alert('Failed to load records for sync');
                },
            });
    }
}
